import { create } from 'zustand';

/** `info` tells without alarming (Add to Yaruta's "already added", YAR-556). */
export type ToastVariant = 'default' | 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onAction(): void;
}

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  /** Second line under the message. */
  description?: string;
  /** No auto-dismiss; the ✕ (or the action) still closes it. */
  persistent?: boolean;
  /** One button on the toast; tapping it runs `onAction` and closes it. */
  action?: ToastAction;
  /** Auto-dismiss time when not the default. */
  durationMs?: number;
}

export interface ToastOptions {
  description?: string;
  persistent?: boolean;
  action?: ToastAction;
  /** Auto-dismiss after this many ms (default 3000). Ignored when `persistent`. */
  durationMs?: number;
}

interface ToastStoreState {
  toasts: ToastItem[];
}

interface ToastStoreActions {
  /** Returns the toast's id, so the caller can close it. */
  addToast: (
    variant: ToastVariant,
    message: string,
    opts?: ToastOptions,
  ) => string;
  removeToast: (id: string) => void;
}

const AUTO_DISMISS_MS = 3000;

const timers = new Map<string, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastStoreState & ToastStoreActions>()(
  (set) => ({
    toasts: [],
    addToast: (variant, message, opts) => {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2);
      const item: ToastItem = { id, variant, message };
      if (opts?.description !== undefined) item.description = opts.description;
      if (opts?.persistent) item.persistent = true;
      if (opts?.action) item.action = opts.action;
      if (opts?.durationMs !== undefined) item.durationMs = opts.durationMs;
      set((state) => ({
        toasts: [...state.toasts, item],
      }));
      if (!item.persistent) {
        const timer = setTimeout(() => {
          timers.delete(id);
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          }));
        }, item.durationMs ?? AUTO_DISMISS_MS);
        timers.set(id, timer);
      }
      return id;
    },
    removeToast: (id) => {
      const timer = timers.get(id);
      if (timer) {
        clearTimeout(timer);
        timers.delete(id);
      }
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    },
  }),
);

/** Convenience helpers — call from anywhere without hooks. Each returns the toast id. */
export const toast = {
  success: (message: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast('success', message, opts),
  error: (message: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast('error', message, opts),
  default: (message: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast('default', message, opts),
  info: (message: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast('info', message, opts),
};
